import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../components/SubscriptionGuard';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getInitials } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { 
  Building2,
  Plus,
  Search,
  Mail,
  Phone,
  MapPin,
  FileText,
  Clock,
  FolderKanban,
  Users,
  Trash2,
  Archive,
  ArchiveRestore,
  Edit,
  ChevronRight,
  ArrowLeft,
  Download,
  ChevronDown,
  CalendarIcon,
  MessageSquare,
  StickyNote,
  Activity,
  MoreHorizontal,
  AlertTriangle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientsManagement() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  
  // State
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total_clients: 0, active_clients: 0, total_hours_this_month: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    notes: '',
    status: 'active',
    gst_id: '',
    gst_treatment: '',
    currency: 'INR'
  });

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('status', statusFilter);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const res = await fetch(`${API_URL}/api/admin/clients?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [token, searchQuery, statusFilter, sortBy, sortOrder]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setLoading(true);
      Promise.all([fetchClients(), fetchStats()]).finally(() => setLoading(false));
    }
  }, [token, fetchClients, fetchStats]);

  // Handlers
  const resetForm = () => {
    setFormData({
      name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      notes: '',
      status: 'active',
      gst_id: '',
      gst_treatment: '',
      currency: 'INR'
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Client name is required');
      return false;
    }
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.gst_treatment) {
      toast.error('GST treatment is required');
      return false;
    }
    if (!formData.currency) {
      toast.error('Currency is required');
      return false;
    }
    if (formData.gst_treatment === 'registered' && !formData.gst_id?.trim()) {
      toast.error('GST ID is required for registered clients');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create client');
      }

      toast.success('Client created successfully');
      setCreateModalOpen(false);
      resetForm();
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = async () => {
    if (!validateForm()) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${selectedClient.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update client');
      }

      toast.success('Client updated successfully');
      setEditModalOpen(false);
      setSelectedClient(null);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleArchive = async (client) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${client.id}/archive`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to archive client');

      toast.success('Client archived');
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRestore = async (client) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${client.id}/restore`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to restore client');

      toast.success('Client restored');
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${selectedClient.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete client');

      toast.success('Client deleted');
      setDeleteModalOpen(false);
      setSelectedClient(null);
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/export/report?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `client_report_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      contact_name: client.contact_name || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      address: client.address || '',
      notes: client.notes || '',
      status: client.status,
      gst_id: client.gst_id || '',
      gst_treatment: client.gst_treatment || '',
      currency: client.currency || 'INR'
    });
    setEditModalOpen(true);
  };

  // Helpers
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'inactive': return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300';
      case 'archived': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('clients.title')}</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{t('clients.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isFreePlan && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleExport('csv')}>
                  CSV
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleExport('pdf')}>
                  PDF
                </Button>
              </PopoverContent>
            </Popover>
            )}
            {user?.role === 'admin' && (
              <Button onClick={() => { resetForm(); setCreateModalOpen(true); }} data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="total-clients">{stats.total_clients}</p>
                  <p className="text-sm text-slate-500">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="active-clients">{stats.active_clients}</p>
                  <p className="text-sm text-slate-500">Active Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="total-hours">{stats.total_hours_this_month || 0}h</p>
                  <p className="text-sm text-slate-500">Hours This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by client name or contact..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px]" data-testid="sort-by">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="hours_this_month">Hours This Month</SelectItem>
                  <SelectItem value="created_at">Date Created</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">A-Z</SelectItem>
                  <SelectItem value="desc">Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contact</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Projects</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Hours (Month)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {clients.map(client => (
                    <tr key={client.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`client-row-${client.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/clients/${client.id}`} className="flex items-center gap-3 group">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                              {getInitials(client.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">
                            {client.name}
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {client.contact_name && <p className="text-slate-700 dark:text-slate-300">{client.contact_name}</p>}
                          {client.contact_email && <p className="text-slate-500 text-xs">{client.contact_email}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={getStatusBadge(client.status)}>{client.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-slate-700 dark:text-slate-300">
                        {client.active_projects || client.project_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-slate-700 dark:text-slate-300">
                        {client.hours_this_month || 0}h
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-menu-${client.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-40">
                            <Link to={`/admin/clients/${client.id}`}>
                              <Button variant="ghost" className="w-full justify-start">
                                <ChevronRight className="w-4 h-4 mr-2" />
                                View Details
                              </Button>
                            </Link>
                            {user?.role === 'admin' && (
                              <>
                                <Button variant="ghost" className="w-full justify-start" onClick={() => openEditModal(client)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </Button>
                                {client.status !== 'archived' ? (
                                  <Button variant="ghost" className="w-full justify-start" onClick={() => handleArchive(client)}>
                                    <Archive className="w-4 h-4 mr-2" />
                                    Archive
                                  </Button>
                                ) : (
                                  <Button variant="ghost" className="w-full justify-start" onClick={() => handleRestore(client)}>
                                    <ArchiveRestore className="w-4 h-4 mr-2" />
                                    Restore
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/30"
                                  onClick={() => { setSelectedClient(client); setDeleteModalOpen(true); }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </Button>
                              </>
                            )}
                          </PopoverContent>
                        </Popover>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clients.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No clients found</p>
                  <Button variant="link" onClick={() => { resetForm(); setCreateModalOpen(true); }}>
                    Create your first client
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={createModalOpen || editModalOpen} onOpenChange={(open) => {
          if (!open) {
            setCreateModalOpen(false);
            setEditModalOpen(false);
            setSelectedClient(null);
            resetForm();
          }
        }}>
          <DialogContent data-testid="client-modal" className="max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editModalOpen ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              <DialogDescription>
                {editModalOpen ? 'Update client information' : 'Create a new client account'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Corporation"
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="john@acme.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City, Country"
                  rows={2}
                  data-testid="address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes about this client..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency {editModalOpen && <span className="text-xs text-slate-400 font-normal ml-1">(locked after creation)</span>}</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })} disabled={editModalOpen}>
                    <SelectTrigger data-testid="currency-select" className={editModalOpen ? 'opacity-60 cursor-not-allowed' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                      <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                      <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                      <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>GST Treatment</Label>
                  <Select value={formData.gst_treatment || 'none'} onValueChange={(v) => setFormData({ ...formData, gst_treatment: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="gst-treatment-select">
                      <SelectValue placeholder="Select GST treatment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Applicable</SelectItem>
                      <SelectItem value="registered">Registered (Has GSTIN)</SelectItem>
                      <SelectItem value="consumer">Consumer (Unregistered)</SelectItem>
                      <SelectItem value="overseas">Overseas (Export)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(formData.gst_treatment === 'registered') && (
                <div className="space-y-2">
                  <Label>GST ID *</Label>
                  <Input
                    value={formData.gst_id}
                    onChange={(e) => setFormData({ ...formData, gst_id: e.target.value })}
                    placeholder="e.g. 29ABCDE1234F1Z5"
                    data-testid="gst-id-input"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateModalOpen(false);
                setEditModalOpen(false);
                setSelectedClient(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button onClick={editModalOpen ? handleEdit : handleCreate} data-testid="save-client-btn">
                {editModalOpen ? 'Save Changes' : 'Create Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent data-testid="delete-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Client
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{selectedClient?.name}</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg text-sm text-red-700 dark:text-red-300">
                <p className="font-medium mb-2">Warning: This action cannot be undone!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All project associations with this client will be removed</li>
                  <li>Client notes and activity history will be deleted</li>
                  <li>Time tracking data will remain but without client reference</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteModalOpen(false); setSelectedClient(null); }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
